import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatRelative } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { listAgents } from '../ai/api';
import { listChannels, setChannelBot, type ChannelView } from './api';
import { ChannelDialog } from './ChannelDialog';
import { WhatsAppWebPanel } from './WhatsAppWebPanel';

/**
 * Connected channels.
 *
 * Connecting one is a credential-handling flow of its own, and the API returns a channel's webhook
 * secret exactly once — which is why the secret is never re-read into this list.
 *
 * **The status column had to stop lying** (Phase 108). A channel row carries a `status` written
 * when somebody connects or disables it; it knows nothing about a WhatsApp browser that dropped
 * ten minutes ago. So this screen showed a table saying "Desconectado" directly above a panel
 * saying "Conectado", about the same number — and an operator cannot work out from that whether
 * their WhatsApp is up. Where a channel has a live session, the row now shows what the session is
 * doing, which is the same fact the panel below shows because it is now the same source.
 */

const STATUS_TONE: Record<ChannelView['status'], BadgeTone> = {
  CONNECTED: 'positive',
  CONNECTING: 'warning',
  DISCONNECTED: 'neutral',
  DISABLED: 'neutral',
  ERROR: 'danger',
};

/** The health of a live session, which is a different question from the channel's own status. */
const HEALTH_TONE: Record<NonNullable<ChannelView['session']>['health'], BadgeTone> = {
  HEALTHY: 'positive',
  PAIRING: 'info',
  STALE: 'warning',
  DOWN: 'neutral',
  BLOCKED: 'danger',
};

export function ChannelsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChannelView | null | undefined>(undefined);

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  // Optional: a plan without agents has no bot column, not a broken page.
  const agents = useQuery({
    queryKey: ['ai-agents'],
    queryFn: listAgents,
    staleTime: 60_000,
    retry: false,
  });

  const bot = useMutation({
    mutationFn: (input: { channelId: string; agentId: string | null }) =>
      setChannelBot(input.channelId, input.agentId),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const published = (agents.data?.agents ?? []).filter((agent) => agent.status === 'ACTIVE');

  const columns: Column<ChannelView>[] = [
    {
      key: 'name',
      header: t('channels.name'),
      render: (channel) => (
        <span className="cell-stack">
          <span className="strong">{channel.name}</span>
          <span className="muted small">{channel.address ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('channels.kind'),
      text: (channel) => t(`channels.kind.${channel.kind}`),
      render: (channel) => <Badge tone="info">{t(`channels.kind.${channel.kind}`)}</Badge>,
    },
    {
      /*
       * The connector, in words (Phase 108).
       *
       * It used to print `whatsapp_web` in a code font — an internal identifier, in front of
       * somebody who is deciding whether their WhatsApp works. The distinction it carries is real
       * and worth showing: the official API and a paired phone behave differently and fail
       * differently. So it is shown, named.
       */
      key: 'provider',
      header: t('channels.provider'),
      secondary: true,
      text: (channel) => t(`channels.providerName.${channel.provider}`),
      render: (channel) => (
        <span className="muted small">{t(`channels.providerName.${channel.provider}`)}</span>
      ),
    },
    {
      /*
       * Which agent answers here (Phase 24).
       *
       * A native select is fine for this one: agents have no faces, and the list is short. Only
       * published agents are offered, because a channel that "has a bot" which never answers is
       * worse than one that plainly has none.
       */
      key: 'bot',
      header: t('channels.bot'),
      render: (channel) => (
        <select
          className="select"
          value={channel.botAgentId ?? ''}
          disabled={bot.isPending}
          onChange={(event) =>
            bot.mutate({ channelId: channel.id, agentId: event.target.value || null })
          }
        >
          <option value="">{t('channels.noBot')}</option>
          {published.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'status',
      header: t('channels.status'),
      text: (channel) =>
        channel.session ? sessionLabel(channel.session) : t(`channels.state.${channel.status}`),
      render: (channel) =>
        channel.session ? (
          <span className="cell-stack">
            <Badge tone={HEALTH_TONE[channel.session.health]}>
              {sessionLabel(channel.session)}
            </Badge>

            {channel.session.lastSeenAt && (
              <span className="muted small">
                {t('whatsappWeb.lastSeen').replace(
                  '{when}',
                  formatRelative(channel.session.lastSeenAt),
                )}
              </span>
            )}

            {channel.session.statusDetail && (
              <span className="muted small">{channel.session.statusDetail}</span>
            )}
          </span>
        ) : (
          <span className="cell-stack">
            <Badge tone={STATUS_TONE[channel.status]}>
              {t(`channels.state.${channel.status}`)}
            </Badge>
            {channel.statusDetail && <span className="muted small">{channel.statusDetail}</span>}
          </span>
        ),
    },
    {
      key: 'default',
      header: t('channels.default'),
      secondary: true,
      render: (channel) => (channel.isDefault ? t('common.yes') : '—'),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      align: 'end',
      render: (channel) => formatDate(channel.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (channel) => (
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => setEditing(channel)}
        >
          {t('channels.edit')}
        </button>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.channels')}
        description={t('channels.description')}
        actions={
          <button type="button" className="button button--primary" onClick={() => setEditing(null)}>
            + {t('channels.connect')}
          </button>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel>
        <DataTable
          tableKey="channels"
          rows={channels.data?.channels}
          columns={columns}
          rowKey={(channel) => channel.id}
          isLoading={channels.isLoading}
          error={channels.error}
          onRetry={() => void channels.refetch()}
          empty={{ title: t('channels.emptyTitle'), description: t('channels.emptyBody') }}
        />
      </Panel>

      {/* Only renders when a channel actually uses the unofficial route, so the warning it carries
          is not shown to tenants who have nothing to be warned about. */}
      <WhatsAppWebPanel channels={channels.data?.channels ?? []} />

      {editing !== undefined && (
        <ChannelDialog channel={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}

/**
 * What to call a session in one badge (Phase 136).
 *
 * `PAIRING` covers both halves of pairing, and calling the second one *Aguardando leitura* tells
 * somebody whose phone already says "connected" that nothing has happened — so they scan again, or
 * conclude the product is broken. Which is what the first number paired in production did.
 */
function sessionLabel(session: NonNullable<ChannelView['session']>): string {
  if (session.stage === 'SYNCING') return t('whatsappWeb.health.SYNCING');

  return t(`whatsappWeb.health.${session.health}`);
}
